import React, { useState, useEffect } from "react";
import {
  TextField,
  Container,
  Grid,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import { fetchThingDetails } from "features/thing/thingThunk";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const JobRoleUpload = () => {
  const dispatch = useDispatch();
  const things = useSelector((state) => state.things.filterThing || []);

  const [formData, setFormData] = useState({
    vehicleNumber: "",
    jobWorkDate: new Date().toISOString().split("T")[0],
    jobWorkType: "",
    description: "",
    cost: "",
    serviceProvider: "",
    odometerReading: "",
    timeTaken: "",
    uploadInvoice: null,
  });

  const [jobWorkTypes, setJobWorkTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Snackbar states
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [warningSB, setWarningSB] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  const openSuccessSB = (msg) => {
    setSnackbarMsg(msg);
    setSuccessSB(true);
  };
  const openErrorSB = (msg) => {
    setSnackbarMsg(msg);
    setErrorSB(true);
  };
  const openWarningSB = (msg) => {
    setSnackbarMsg(msg);
    setWarningSB(true);
  };
  const closeSuccessSB = () => setSuccessSB(false);
  const closeErrorSB = () => setErrorSB(false);
  const closeWarningSB = () => setWarningSB(false);

  // ✅ Fetch vehicle list
  useEffect(() => {
    dispatch(fetchThingDetails());
  }, [dispatch]);

  // ✅ Fetch Job Work Types
  useEffect(() => {
    const fetchJobWorkTypes = async () => {
      const token = localStorage.getItem("token");
      setLoading(true);
      try {
        const response = await axios.get(
          "https://v3.dhundhoo.com/jobwork/jobWorkTypes",
          {
            headers: { "x-dhundhoo-session": token },
          }
        );

        const jobTypesArray = Object.entries(response.data).map(([key, value]) => ({
          key,
          label: value,
        }));
        setJobWorkTypes(jobTypesArray);
      } catch (error) {
        console.error("Error fetching job work types:", error);
        openErrorSB("Failed to load job work types");
      } finally {
        setLoading(false);
      }
    };
    fetchJobWorkTypes();
  }, []);

  // ✅ Handle Input Change
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "uploadInvoice") {
      setFormData({ ...formData, uploadInvoice: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // ✅ Submit Job Work Form
  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      vehicleNumber,
      jobWorkDate,
      jobWorkType,
      description,
      cost,
      serviceProvider,
      odometerReading,
      timeTaken,
      uploadInvoice,
    } = formData;

    if (!vehicleNumber || !jobWorkDate || !jobWorkType || !description || !cost) {
      openWarningSB("Please fill all required fields");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      openErrorSB("Session missing. Please login again.");
      return;
    }

    const jsonData = {
      vehicleId: vehicleNumber,
      jobWorkDate: new Date(jobWorkDate).toISOString(),
      jobWorkType,
      description,
      cost: parseFloat(cost),
      serviceProvider,
      odometerReading: parseInt(odometerReading || 0, 10),
      timeTaken: parseFloat(timeTaken || 0),
      invoiceUpload: uploadInvoice ? uploadInvoice.name : undefined,
    };

    const submitData = new FormData();
    submitData.append(
      "data",
      new Blob([JSON.stringify(jsonData)], { type: "application/json" })
    );
    if (uploadInvoice) {
      submitData.append("file", uploadInvoice);
    }

    setIsSubmitting(true);

    try {
      const res = await axios.post("https://v3.dhundhoo.com/jobwork", submitData, {
        headers: { "x-dhundhoo-session": token },
        maxBodyLength: Infinity,
      });

      if (res.data && res.data.success) {
        openSuccessSB("Job Work saved successfully!");
        handleClear();
      } else {
        openErrorSB(res.data?.message || "Failed to save Job Work");
      }
    } catch (error) {
      console.error("Error submitting Job Work:", error);
      openErrorSB(
        error.response?.data?.message || "Error submitting form. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Clear Form
  const handleClear = () => {
    setFormData({
      vehicleNumber: "",
      jobWorkDate: new Date().toISOString().split("T")[0],
      jobWorkType: "",
      description: "",
      cost: "",
      serviceProvider: "",
      odometerReading: "",
      timeTaken: "",
      uploadInvoice: null,
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Container
        maxWidth={false}
        sx={{ display: "flex", justifyContent: "center", padding: 2 }}
      >
        <MDBox
          sx={{
            width: "100%",
            maxWidth: "900px",
            padding: 3,
            borderRadius: 2,
          }}
        >
          <MDTypography
            variant="h5"
            fontWeight="600"
            gutterBottom
            sx={{
              color: "#1a237e",
              mb: 4,
              textAlign: "center",
            }}
          >
            Upload Job Role
          </MDTypography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Vehicle */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={Array.isArray(things) ? things : []}
                  getOptionLabel={(option) => option?.name || ""}
                  value={
                    formData.vehicleNumber
                      ? things.find((v) => v.id === formData.vehicleNumber) || null
                      : null
                  }
                  onChange={(event, newValue) =>
                    setFormData({
                      ...formData,
                      vehicleNumber: newValue ? newValue.id : "",
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vehicle Number"
                      fullWidth
                      size="small"
                      required
                      sx={{ backgroundColor: "white", borderRadius: 1 }}
                    />
                  )}
                />
              </Grid>

              {/* Job Work Type */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={jobWorkTypes}
                  getOptionLabel={(option) => option.label || ""}
                  loading={loading}
                  value={
                    jobWorkTypes.find(
                      (type) => type.key === formData.jobWorkType
                    ) || null
                  }
                  onChange={(event, newValue) =>
                    setFormData({
                      ...formData,
                      jobWorkType: newValue ? newValue.key : "",
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Job Work Type"
                      fullWidth
                      size="small"
                      required
                      sx={{ backgroundColor: "white", borderRadius: 1 }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Job Work Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Job Work Date"
                  name="jobWorkDate"
                  type="date"
                  value={formData.jobWorkDate}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ backgroundColor: "white", borderRadius: 1 }}
                />
              </Grid>

              {/* Cost */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Cost"
                  name="cost"
                  type="number"
                  value={formData.cost}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{ backgroundColor: "white", borderRadius: 1 }}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={1}
                  required
                  sx={{ backgroundColor: "white", borderRadius: 1 }}
                />
              </Grid>

              {/* Service Provider */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Service Provider"
                  name="serviceProvider"
                  value={formData.serviceProvider}
                  onChange={handleChange}
                  fullWidth
                  sx={{ backgroundColor: "white", borderRadius: 1 }}
                />
              </Grid>

              {/* Odometer */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Odometer Reading"
                  name="odometerReading"
                  type="number"
                  value={formData.odometerReading}
                  onChange={handleChange}
                  fullWidth
                  sx={{ backgroundColor: "white", borderRadius: 1 }}
                />
              </Grid>

              {/* Time Taken */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Time Taken (Hours)"
                  name="timeTaken"
                  type="number"
                  value={formData.timeTaken}
                  onChange={handleChange}
                  fullWidth
                  sx={{ backgroundColor: "white", borderRadius: 1 }}
                />
              </Grid>

              {/* File Upload */}
              <Grid item xs={12} sm={6}>
                <MDButton
                  variant="outlined"
                  component="label"
                  fullWidth
                  color="info"
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 1,
                    textTransform: "none",
                  }}
                >
                  {formData.uploadInvoice
                    ? `Selected: ${formData.uploadInvoice.name}`
                    : "Select File"}
                  <input
                    type="file"
                    name="uploadInvoice"
                    accept=".pdf,.jpg,.png"
                    hidden
                    onChange={handleChange}
                  />
                </MDButton>
              </Grid>
            </Grid>

            {/* Buttons */}
            <MDBox sx={{ display: "flex", justifyContent: "flex-start", mt: 3 }}>
              <MDButton
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: "#fb8c00",
                  color: "white !important",
                  "&:hover": { backgroundColor: "#fb8c00" },
                  mr: 2,
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </MDButton>

              <MDButton
                variant="outlined"
                onClick={handleClear}
                sx={{
                  borderColor: "#666",
                  color: "#666",
                  "&:hover": { borderColor: "#555", color: "#555" },
                }}
              >
                Clear
              </MDButton>
            </MDBox>
          </form>
        </MDBox>

        {/* ✅ Snackbars */}
        <MDSnackbar
          color="success"
          icon="check"
          title="Job Work"
          content={snackbarMsg}
          open={successSB}
          onClose={closeSuccessSB}
          close={closeSuccessSB}
          bgWhite
        />

        <MDSnackbar
          color="error"
          icon="warning"
          title="Error"
          content={snackbarMsg}
          open={errorSB}
          onClose={closeErrorSB}
          close={closeErrorSB}
          bgWhite
        />

        <MDSnackbar
          color="warning"
          icon="info"
          title="Warning"
          content={snackbarMsg}
          open={warningSB}
          onClose={closeWarningSB}
          close={closeWarningSB}
          bgWhite
        />
      </Container>
      </DashboardLayout>
  );
};

export default JobRoleUpload;